import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehicleApi } from '../../lib/api';
import toast from 'react-hot-toast';

interface VehicleForm {
  make: string; model: string; year: number;
  vehicle_type: string; category: string;
  license_plate: string; color: string; mileage: string;
  daily_rate: string; weekly_discount_pct: string; monthly_discount_pct: string;
  deposit_required: string; location_city: string; location_district: string;
  can_drive_public_road: boolean; carrier_available: boolean;
  description: string; features: string;
}

const INITIAL_FORM: VehicleForm = {
  make: '', model: '', year: new Date().getFullYear(),
  vehicle_type: 'sedan', category: 'prop_car',
  license_plate: '', color: '', mileage: '',
  daily_rate: '', weekly_discount_pct: '0', monthly_discount_pct: '0',
  deposit_required: '0', location_city: '서울', location_district: '',
  can_drive_public_road: false, carrier_available: false,
  description: '', features: '',
};

const MAX_PHOTOS = 10;
const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10MB

export default function VehicleRegisterPage() {
  const API_BASE = window.location.hostname === 'localhost'
    ? '/api'
    : 'https://purplemine.mkim0818.workers.dev/api';
  const navigate = useNavigate();
  const [form, setForm] = useState<VehicleForm>(INITIAL_FORM);
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const update = (key: keyof VehicleForm, value: VehicleForm[typeof key]) =>
    setForm(f => ({ ...f, [key]: value }));

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`최대 ${MAX_PHOTOS}장까지 업로드 가능합니다`);
      return;
    }
    const valid: File[] = [];
    for (const f of files) {
      if (!f.type.startsWith('image/')) { toast.error(`${f.name}은 이미지가 아닙니다`); continue; }
      if (f.size > MAX_PHOTO_SIZE) { toast.error(`${f.name}은 10MB를 초과합니다`); continue; }
      valid.push(f);
    }
    const newPhotos = [...photos, ...valid];
    setPhotos(newPhotos);
    setPreviews(newPhotos.map(f => URL.createObjectURL(f)));
    // reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (idx: number) => {
    URL.revokeObjectURL(previews[idx]);
    setPhotos(p => p.filter((_, i) => i !== idx));
    setPreviews(p => p.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('carp2p_token');
      const body = JSON.stringify({
        make: form.make,
        model: form.model,
        year: form.year,
        vehicle_type: form.vehicle_type,
        category: form.category,
        license_plate: form.license_plate || undefined,
        color: form.color || undefined,
        mileage: form.mileage ? parseInt(form.mileage) : undefined,
        daily_rate: parseInt(form.daily_rate),
        weekly_discount_pct: parseInt(form.weekly_discount_pct) || 0,
        monthly_discount_pct: parseInt(form.monthly_discount_pct) || 0,
        deposit_required: parseInt(form.deposit_required) || 0,
        location_city: form.location_city,
        location_district: form.location_district || undefined,
        can_drive_public_road: form.can_drive_public_road,
        carrier_available: form.carrier_available,
        description: form.description || undefined,
        features: form.features || undefined,
      });

      const res = await fetch(`${API_BASE}/vehicles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err && typeof err === 'object' && 'error' in err) ? String(err.error) : '등록 실패');
      }

      const created = await res.json();
      const vehicleId: string | undefined = created && typeof created === 'object' && 'id' in created ? String(created.id) : undefined;

      if (vehicleId && photos.length > 0) {
        let uploaded = 0;
        for (let i = 0; i < photos.length; i++) {
          try {
            const fd = new FormData();
            fd.append('file', photos[i]);
            fd.append('photo_type', 'exterior');
            if (i === 0) fd.append('is_primary', '1');
            await vehicleApi.uploadPhoto(vehicleId, fd);
            uploaded++;
          } catch {
            // continue with remaining photos
          }
        }
        if (uploaded > 0) toast.success(`차량 등록 완료! 사진 ${uploaded}장 업로드됨`);
        else toast.success('차량이 등록되었습니다. 관리자 승인 후 노출됩니다.');
      } else {
        toast.success('차량이 등록되었습니다. 관리자 승인 후 노출됩니다.');
      }

      navigate('/host/vehicles');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '등록에 실패했습니다';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const textInput = (label: string, key: keyof VehicleForm, type = 'text', required = false) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && ' *'}</label>
      <input
        type={type}
        className="input-field"
        value={String(form[key])}
        onChange={e => {
          const val = e.target.value;
          if (type === 'number') {
            const n = parseInt(val);
            update(key, isNaN(n) ? ('' as never) : n);
          } else {
            update(key, val);
          }
        }}
        required={required}
      />
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">차량 등록</h1>
      <p className="text-sm text-gray-500 mb-8">등록 후 관리자 승인을 거쳐 게스트에게 노출됩니다. 차량 소유를 증명할 수 있는 서류를 준비해주세요.</p>

      <form onSubmit={handleSubmit} className="card p-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {textInput('제조사', 'make', 'text', true)}
          {textInput('모델명', 'model', 'text', true)}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {textInput('연식', 'year', 'number', true)}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">차종 *</label>
            <select className="input-field" value={form.vehicle_type} onChange={e => update('vehicle_type', e.target.value)}>
              <option value="sedan">세단</option><option value="suv">SUV</option><option value="truck">트럭</option>
              <option value="van">밴</option><option value="sports">스포츠카</option><option value="classic">클래식카</option>
              <option value="special">특수차량</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">구분 *</label>
            <select className="input-field" value={form.category} onChange={e => update('category', e.target.value)}>
              <option value="prop_car">소품차 (전시장 제한 주행)</option>
              <option value="rental_car">렌트카 (일반도로 주행 가능)</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {textInput('차량번호', 'license_plate')}
          {textInput('색상', 'color')}
          {textInput('주행거리(km)', 'mileage', 'number')}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {textInput('일일 대여료 (원)', 'daily_rate', 'number', true)}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">지역 *</label>
            <select className="input-field" value={form.location_city} onChange={e => update('location_city', e.target.value)}>
              <option value="서울">서울</option><option value="경기">경기</option><option value="인천">인천</option>
              <option value="부산">부산</option><option value="대구">대구</option><option value="광주">광주</option>
              <option value="대전">대전</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {textInput('7일+ 할인율 (%)', 'weekly_discount_pct', 'number')}
          {textInput('30일+ 할인율 (%)', 'monthly_discount_pct', 'number')}
        </div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.can_drive_public_road} onChange={e => update('can_drive_public_road', e.target.checked)}
              className="focus:outline-none focus:ring-2 focus:ring-brand-500 rounded" />
            일반도로 주행 가능
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.carrier_available} onChange={e => update('carrier_available', e.target.checked)}
              className="focus:outline-none focus:ring-2 focus:ring-brand-500 rounded" />
            캐리어 탁송 가능
          </label>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">차량 설명</label>
          <textarea className="input-field" rows={3} value={form.description} onChange={e => update('description', e.target.value)}
            placeholder="차량 상태, 촬영 경력 등" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">특징 및 출연 작품</label>
          <textarea className="input-field" rows={2} value={form.features} onChange={e => update('features', e.target.value)}
            placeholder="영화 ○○○ 출연, 드라마 ○○○ 등" />
        </div>

        {/* Photo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            차량 사진 <span className="text-gray-400 font-normal">({photos.length}/{MAX_PHOTOS})</span>
          </label>
          {previews.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-3">
              {previews.map((src, i) => (
                <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 group">
                  <img src={src} alt={`미리보기 ${i + 1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)}
                    className="absolute top-0 right-0 w-6 h-6 bg-black/60 text-white text-xs rounded-bl-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoSelect}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4
              file:rounded-lg file:border-0 file:text-sm file:font-semibold
              file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100
              cursor-pointer"
          />
          <p className="text-xs text-gray-400 mt-1">10MB 이하 이미지, 최대 {MAX_PHOTOS}장. 첫 번째 사진이 대표 이미지로 설정됩니다.</p>
        </div>

        <button type="submit" className="btn-primary w-full" disabled={submitting}>
          {submitting ? '등록 중...' : '등록하기'}
        </button>
      </form>
    </div>
  );
}
